import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Coins, Zap, Loader2, ArrowRight } from "lucide-react";

export default function DashCredits() {
  const { user } = useAuth();
  const { t, lang, formatNumber, formatCurrency } = useLang();
  const [txs, setTxs] = React.useState([]);
  const [creemStatus, setCreemStatus] = React.useState(null);
  const [amount, setAmount] = React.useState(50);
  const [quote, setQuote] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [acceptTerms, setAcceptTerms] = React.useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = React.useState(false);
  const [amountError, setAmountError] = React.useState("");

  const loadTxs = () => api.get("/api/dashboard/credits/transactions").then((r) => setTxs(r.data.items || []));

  React.useEffect(() => {
    loadTxs();
    api.get("/api/creem/status").then((r) => setCreemStatus(r.data));
  }, []);

  // Integer-only USD amounts (min $1, max $5000 by default).
  const parsedAmount = React.useMemo(() => {
    if (amount === "" || amount === null || amount === undefined) return NaN;
    const n = Number(amount);
    if (!Number.isFinite(n)) return NaN;
    if (!Number.isInteger(n)) return NaN;
    return n;
  }, [amount]);

  React.useEffect(() => {
    if (!creemStatus) return;
    const min = creemStatus.min_topup_usd || 1;
    const max = creemStatus.max_topup_usd || 5000;
    if (Number.isNaN(parsedAmount)) {
      setAmountError(t("dash.credits.topup.integer_only") || "Enter a whole USD amount.");
      setQuote(null);
      return;
    }
    if (parsedAmount < min || parsedAmount > max) {
      setAmountError("");
      setQuote(null);
      return;
    }
    setAmountError("");
    let cancelled = false;
    const timeout = setTimeout(async () => {
      try {
        const { data } = await api.post("/api/creem/quote/topup", { amount_usd: parsedAmount, language: lang });
        if (!cancelled) setQuote(data);
      } catch {
        if (!cancelled) setQuote(null);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [parsedAmount, creemStatus, lang, t]);

  const checkout = async () => {
    if (!quote) return;
    if (!acceptTerms || !acceptPrivacy) {
      toast.error(t("checkout.consent.required"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/api/creem/checkout/topup", {
        amount_usd: parsedAmount,
        language: lang,
        accept_terms: acceptTerms,
        accept_privacy: acceptPrivacy,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Missing checkout URL from Creem");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const range = creemStatus ? t("dash.credits.topup.range", {
    min: `$${creemStatus.min_topup_usd}`,
    max: `$${(creemStatus.max_topup_usd || 5000).toLocaleString("en-US")}`,
  }) : "";
  return (
    <div className="space-y-6" data-testid="dash-credits-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-white/95">{t("dash.credits.title")}</h1>
          <p className="text-white/50 text-sm mt-1">{t("dash.credits.subtitle")}</p>
        </div>
        <div className="lp-glass p-4">
          <div className="text-[10px] uppercase tracking-widest text-white/40">{t("dash.credits.balance")}</div>
          <div className="font-display text-2xl text-[#d4af37]" data-testid="credits-balance">{formatNumber(user?.credits ?? 0)}</div>
        </div>
      </div>

      <div className="lp-card p-6 grid lg:grid-cols-[1.1fr_1fr] gap-8">
        <div>
          <h3 className="font-display text-xl text-white/95">{t("dash.credits.topup.title")}</h3>
          <p className="text-white/50 text-sm mt-1">{t("dash.credits.topup.subtitle")}</p>
          <label className="text-xs uppercase tracking-widest text-white/50 mt-6 mb-2 block">{t("dash.credits.topup.amount_label")}</label>
          <div className="flex items-stretch rounded-xl border border-white/12 bg-[rgba(10,11,14,0.6)] focus-within:border-[#d4af37] focus-within:shadow-[0_0_0_3px_rgba(212,175,55,0.14)] transition-colors">
            <span className="flex items-center justify-center w-11 text-white/55 text-sm font-mono border-r border-white/10 select-none" aria-hidden="true">$</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              min={creemStatus?.min_topup_usd || 1}
              max={creemStatus?.max_topup_usd || 5000}
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ""))}
              className="flex-1 bg-transparent border-0 outline-none px-3 py-3 text-white placeholder:text-white/40 text-base"
              data-testid="topup-amount"
              aria-label="USD amount"
            />
          </div>
          <div className="text-xs text-white/40 mt-2">{range}</div>
          {amountError && (
            <div className="text-xs text-red-300 mt-2" data-testid="topup-amount-error">{amountError}</div>
          )}
          <div className="mt-4 grid grid-cols-4 gap-2">
            {[5, 25, 100, 500].map((v) => (
              <button
                key={v}
                onClick={() => setAmount(v)}
                className="lp-btn-secondary py-2 justify-center text-sm"
                data-testid={`preset-${v}`}
              >${v}</button>
            ))}
          </div>
          <button
            onClick={checkout}
            disabled={!quote || busy || !acceptTerms || !acceptPrivacy}
            className="lp-btn-primary w-full justify-center mt-6"
            data-testid="topup-checkout"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            {t("dash.credits.topup.button")} <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-4 space-y-2 text-sm text-white/70" data-testid="checkout-consent">
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} data-testid="topup-accept-terms" />
              <span>
                {t("checkout.consent.terms.pre")}{" "}
                <Link to="/legal/terms" target="_blank" rel="noreferrer" className="text-[#d4af37] underline">{t("checkout.consent.terms.link")}</Link>{" "}
                {t("checkout.consent.and")}{" "}
                <Link to="/legal/privacy" target="_blank" rel="noreferrer" className="text-[#d4af37] underline">{t("checkout.consent.privacy.link")}</Link>.
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input type="checkbox" checked={acceptPrivacy} onChange={(e) => setAcceptPrivacy(e.target.checked)} data-testid="topup-accept-privacy" />
              <span>{t("checkout.mor.notice")} <a href="https://www.creem.io/legal/buyer-terms" target="_blank" rel="noreferrer" className="text-[#d4af37] underline">{t("checkout.mor.link")}</a>.</span>
            </label>
          </div>
          <p className="text-xs text-white/40 mt-3">{t("dash.credits.topup.note")}</p>
        </div>

        <div className="lp-glass p-6">
          <div className="text-[10px] uppercase tracking-widest text-white/40">{t("dash.credits.topup.credits_label")}</div>
          {quote ? (
            <>
              <div className="font-display text-4xl text-[#d4af37] mt-2" data-testid="quote-total">
                {formatNumber(quote.total_credits)}
              </div>
              <div className="text-xs text-white/50 mt-1" data-testid="quote-rate">
                {t("dash.credits.topup.rate", { per: formatNumber(quote.credits_per_usd || 1000) }) || `1 USD = ${formatNumber(quote.credits_per_usd || 1000)} credits`}
              </div>
              <div className="mt-4 h-px bg-white/8" />
              <div className="mt-4 flex items-center justify-between text-sm text-white/70">
                <span>{formatCurrency(quote.amount_usd, "USD")}</span>
                <span className="text-white/40 font-mono text-xs">Creem</span>
              </div>
              <div className="mt-2 text-[10px] text-white/45" data-testid="topup-tax-suffix">
                {t("pricing.tax.suffix")}
              </div>
              <p className="mt-3 text-[11px] text-white/40 leading-relaxed" data-testid="topup-tax-note">
                {t("pricing.tax.note")}
              </p>
            </>
          ) : (
            <div className="text-white/50 text-sm mt-4">{range}</div>
          )}
        </div>
      </div>

      <div className="lp-card overflow-hidden">
        <div className="p-4 border-b border-white/8 font-display text-white/85">{t("dash.credits.txs.title")}</div>
        <table className="lp-table">
          <thead><tr>
            <th>{t("dash.credits.txs.col.date")}</th>
            <th>{t("dash.credits.txs.col.reason")}</th>
            <th>{t("dash.credits.txs.col.delta")}</th>
            <th>{t("dash.credits.txs.col.balance")}</th>
          </tr></thead>
          <tbody>
            {txs.length === 0 && <tr><td colSpan={4} className="text-center text-white/50 py-8">{t("dash.credits.txs.empty")}</td></tr>}
            {txs.slice(0, 40).map((tx) => (
              <tr key={tx.id}>
                <td className="text-white/60 text-xs">{new Date(tx.created_at).toLocaleString()}</td>
                <td className="text-white/80 capitalize">{tx.reason}</td>
                <td className={tx.delta >= 0 ? "text-emerald-400" : "text-red-300"}>
                  {tx.delta >= 0 ? "+" : ""}{formatNumber(tx.delta)}
                </td>
                <td className="text-white/70 font-mono">{formatNumber(tx.balance_after)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
